import { useParams, useNavigate } from "react-router";
import { useLibraryNode } from "../../lib/hooks/use-kuvio";
import { Card, EmptyState, Pill, Breadcrumb } from "../components/primitives";
import type { Persona, BreadcrumbSegment } from "../components/primitives";
import { Markdown } from "../components/Markdown";
import { PageView } from "./PageView";
import { PromptView } from "./PromptView";
import { useChatScope } from "../hooks/use-chat-scope";
import type { MemoryType } from "../../lib/types";

const MEMORY_TYPE_META: Record<MemoryType, { variant: Persona; label: string }> = {
  user: { variant: "blue", label: "user" },
  feedback: { variant: "amber", label: "feedback" },
  project: { variant: "green", label: "project" },
  reference: { variant: "gray", label: "reference" },
};

function memoryTypeMeta(type: MemoryType | null | undefined): { variant: Persona; label: string } {
  if (type && type in MEMORY_TYPE_META) return MEMORY_TYPE_META[type];
  return { variant: "gray", label: "memory" };
}

export function LibraryMemoryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const node = useLibraryNode(id);

  // Sprint 22 — scope the cockpit chat to this page/memory, with a snippet of the
  // body as the focus anchor (the Page editor + chat side-by-side authoring loop).
  const scoped = node.data;
  useChatScope(
    scoped
      ? { kind: "page", id: scoped.id, label: scoped.name }
      : { kind: "workspace", label: "This workspace" },
    scoped
      ? {
          kind: scoped.type ?? "page",
          id: scoped.id,
          name: scoped.name,
          snippet: (scoped.document ?? "").slice(0, 600) || undefined,
        }
      : null,
    [scoped?.id, scoped?.name, scoped?.type],
  );

  if (node.isLoading) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div className="kuvio-skeleton" style={{ height: 120, borderRadius: "var(--border-radius-lg)", background: "var(--color-background-secondary)" }} />
      </div>
    );
  }

  if (node.error || !node.data) {
    return (
      <Card>
        <EmptyState>{node.error?.message ?? "Memory not found"}</EmptyState>
      </Card>
    );
  }

  // Sprint 5o: /library/:id serves both memories and pages. Pages get the
  // full-page editor; this route stays the memory reader for memory nodes.
  if (node.data.type === "page") {
    return <PageView />;
  }
  // Sprint 11: prompt nodes get their own DOES editor (args + test-compose).
  if (node.data.type === "prompt") {
    return <PromptView />;
  }

  const memory = node.data;
  const meta = memoryTypeMeta(memory.memory_type);
  const body = memory.document ?? "";

  const segments: BreadcrumbSegment[] = (memory._breadcrumb ?? [])
    .filter((b) => b.id !== memory.id)
    .map((b) => ({
      label: b.name,
      onClick: () => b.type === "agent" ? navigate(`/agents/${b.id}`) : navigate(`/library/${b.id}`),
    }));
  segments.push({ label: memory.name });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 720 }}>
      <Breadcrumb segments={segments} />
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, color: "var(--color-text-primary)", margin: 0 }}>
          {memory.name}
        </h1>
        <Pill variant={meta.variant}>{meta.label}</Pill>
      </div>
      <Card>
        {body.trim() ? (
          <Markdown>{body}</Markdown>
        ) : (
          <EmptyState>This memory is empty.</EmptyState>
        )}
      </Card>
    </div>
  );
}
